import React from "react";

import { switchCompanyURL as switchCompanyUrl, baseURL as employerBaseUrl } from "Dashboard/EmployerUrls";
import { setFilter } from "QuotePage/Actions";
import QuotePageShoppingCart from "QuotePage/app/QuotePageShoppingCart";
import QuotePageContext from "QuotePage/Context";
import { changeZIPCodeURL, quotePageName } from "QuotePage/urls";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Dispatch } from "redux";
import isSquare from "Square/isSquare";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import companyToken, { companyTokenString } from "Utils/companyToken";
import { isPartner } from "Utils/isPartner";
import { baseUrl, userSettingsURL as userSettingsUrl, supportUrl } from "ZeroRuby/urls";

import { NavBarDropdown, NavBarMenuItem } from "Cobalt/NavBar";
import { getCartItems } from "Common/CartService";
import { getFlipper } from "Common/flipperUtils";
import { receiveFlipper } from "Common/flipperUtils/redux";
import Icon from "Common/Icon";
import { getSiteLogo } from "Common/Selectors";
import { appendAuthParams } from "Common/TokenAuth/Utils";
import ResponsiveTopBar from "Common/TopBar/ResponsiveTopBar";
import { showUserSettings } from "Common/TopBar/showUserSettings";

const ANALYTICS_LOCATION = "top_bar";

type AccountLinksProps = {
  menuTitle?: string;
  showSwitchEmployerDashboard?: boolean;
  showSwitchCompany?: boolean;
  showUserSettings?: boolean;
  showLogout?: boolean;
};

const AccountMenuTitle = ({ title }: { title?: string }) => (
  <span>
    <Icon icon="user" flavor="solid" spacing="right" />
    <span>{title || "Account"}</span>
  </span>
);

const QuotePageAccountLinks = ({
  menuTitle,
  showSwitchEmployerDashboard,
  showSwitchCompany,
  showUserSettings: showUserSettingsLink,
  showLogout,
}: AccountLinksProps) => (
  <>
    <NavBarMenuItem href={supportUrl}>Support</NavBarMenuItem>
    <NavBarDropdown label={<AccountMenuTitle title={menuTitle} />}>
      {showSwitchEmployerDashboard && (
        <NavBarMenuItem href={appendAuthParams(`${baseUrl}${companyTokenString()}${employerBaseUrl}`)}>Switch to Company Dashboard</NavBarMenuItem>
      )}
      {showSwitchCompany && <NavBarMenuItem href={appendAuthParams(`${baseUrl}${switchCompanyUrl}`)}>Switch Company</NavBarMenuItem>}
      {showUserSettingsLink && <NavBarMenuItem href={appendAuthParams(`${baseUrl}${userSettingsUrl}`)}>User Settings</NavBarMenuItem>}
      {showLogout && <NavBarMenuItem href="/logout">Sign out</NavBarMenuItem>}
    </NavBarDropdown>
  </>
);

type Props = {
  onClickMedical: (renewalFlag?: boolean) => void;
  onClickMEC: () => void;
  showZipCode?: boolean;
  zipCode?: string;
  onClickZipCode: () => void;
  showSwitchCompany?: boolean;
  showUserSettings?: boolean;
  showLogout: boolean;
  greetingName?: string;
  showCompanyDashboardLink: boolean;
  branding?: string;
  partnerLogo?: string;
  renewalFlag?: boolean;
};

class QuotePageTopBarDumb extends React.Component<Props> {
  renderZipCodeNavItem = () => {
    const { zipCode, onClickZipCode, showZipCode } = this.props;

    if (!showZipCode || isPartner()) {
      return false;
    }

    return (
      <NavBarMenuItem onClick={onClickZipCode} href={changeZIPCodeURL({ renewal: false, relative: false })}>
        ZIP Code: {zipCode}
      </NavBarMenuItem>
    );
  };

  renderLeftContent = () => {
    const { onClickMedical, onClickMEC, renewalFlag } = this.props;

    if (!window.location.pathname.includes("select-plan")) {
      return [];
    }

    // Must return an array here - responsive menu will not collapse on select
    // if we return a React.Fragment.
    const leftContent = [this.renderZipCodeNavItem()];

    return leftContent;
  };

  renderRightContent = () => {
    const { greetingName, showSwitchCompany, showCompanyDashboardLink, showUserSettings: showUserSettingsLink, showLogout } = this.props;

    // Must return an array here - responsive menu will not collapse on select
    // if we return a React.Fragment.
    const rightContent = [];

    rightContent.push(
      <QuotePageAccountLinks
        key="accountLinks"
        menuTitle={greetingName}
        showSwitchEmployerDashboard={showCompanyDashboardLink}
        showSwitchCompany={showSwitchCompany}
        showUserSettings={showUserSettingsLink}
        showLogout={showLogout}
      />,
    );

    rightContent.push(React.createElement(QuotePageShoppingCart as unknown as React.ComponentType, { key: "shoppingCart" }));

    return rightContent;
  };

  render() {
    const { branding = "default", partnerLogo } = this.props;

    return (
      <ResponsiveTopBar
        ariaLabel="Quote page navigation"
        branding={branding}
        leftContent={this.renderLeftContent()}
        logoLinkAriaLabel="SimplyInsured home"
        partnerLogo={partnerLogo}
        rightContent={this.renderRightContent()}
      />
    );
  }
}

const mapStateToProps = (state) => {
  const { companyProfile } = state;
  const renewalFlag = !!state.renewalPolicy;
  const zipCode = companyProfile.zip_code_number || (window as any).zipcode;

  const onClickZipCode = () => {
    AnalyticsTracking.trackEvent("change_zip_code_clicked", {
      location: ANALYTICS_LOCATION,
    });
  };

  return {
    showZipCode: !state.renewalPolicy,
    // window.zipcode might be set if we are loading the quote page.
    // quickbooks_controller.render_quote_page
    zipCode,
    onClickZipCode,
    showSwitchCompany: !!companyToken(),
    showUserSettings: showUserSettings(zipCode),

    showLogout: !isSquare(),
    greetingName: companyProfile.name || companyProfile.email,
    showCompanyDashboardLink: !!state.renewalPolicy,
    quoteSummaryItemCount: getCartItems(state),
    branding: state.siteConfig.branding,
    partnerLogo: getSiteLogo(state),
    renewalFlag,
    flipper: state.flipper,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>, { history }) => ({
  onClickMedical: (renewalFlag) => {
    dispatch(setFilter("recommendation_mode", "medical"));
    AnalyticsTracking.trackEvent("plans_viewed", {
      location: ANALYTICS_LOCATION,
      type: "medical",
    });

    history.push(quotePageName(renewalFlag));
  },
  onClickMEC: () => {
    AnalyticsTracking.trackEvent("switched_mode", {
      location: "quote_page",
      mode: "limited_value_plans",
    });
    dispatch(setFilter("recommendation_mode", "limited_value_plans"));
  },
  loadFlipper: (flipperName) => {
    getFlipper(flipperName)
      .then((r) => r.json())
      .then((r) => dispatch(receiveFlipper(r)));
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(QuotePageTopBarDumb));
